import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Icon, Loader, Typography } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { retriggerPreLaunchAnalysis } from '../../../redux/prelaunch/action';
import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages from './messages';
import MetricTable from './MetricTable';
import ProgressBar from './ProgressBar';

import './PreLaunchDashboard.css';

function getPhaseLabel(pct, l) {
  if (pct <= 25) return l('Aggregate Counts');
  if (pct <= 50) return l('Data Quality');
  if (pct <= 75) return l('Risk Analysis');

  return l('Finalizing');
}

function PreLaunchDashboard({ onDrillDown }) {
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);

  const {
    campaignId,
    statusData,
    reportData,
    isReportLoading,
    isTriggerLoading,
    triggerError,
    triggerConflict,
    reportError,
  } = useSelector((s) => s.certifications.prelaunch);

  const handleRetrigger = () => {
    if (!campaignId) return;
    // Navigate only after the retrigger dispatch settles so the in-flight
    // request is not cancelled by the page unload. The retrigger thunk
    // resolves regardless of success/failure, so navigation is unconditional.
    // NOTE: this is a cross-MFE jump to the host ECM campaign list, which is a
    // separate single-spa app — react-router history.push() cannot reach it,
    // so a hard window.location navigation is required here.
    Promise.resolve(dispatch(retriggerPreLaunchAnalysis(campaignId))).finally(
      () => {
        window.location = '/ECM/campaign/list?status=10';
      }
    );
  };

  const isRunning = statusData?.status === 'RUNNING';
  const hasReport = !!reportData;

  // ── RUNNING with existing report — show inline progress overlay ────────
  if (isRunning && hasReport) {
    const pct = statusData.progressPct || 0;

    return (
      <Box className='PreLaunchDashboard'>
        <Box className='PreLaunchDashboard-inlineProgress'>
          <Loader kind='dots' format='inline' size='small' color='brand' />
          <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
            {l('Re-analyzing…')}
          </Typography>
          <ProgressBar
            pct={pct}
            status='RUNNING'
            phaseLabel={getPhaseLabel(pct, l)}
          />
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            className='PreLaunchDashboard-runningHint'>
            {l(
              'This page will automatically refresh when the analysis completes.'
            )}
          </Typography>
        </Box>
        <DashboardContent
          reportData={reportData}
          onDrillDown={onDrillDown}
          l={l}
          isStale
        />
      </Box>
    );
  }

  // ── RUNNING without report — full-page progress ────────────────────────
  if (isRunning) {
    const pct = statusData.progressPct || 0;

    return (
      <Box className='PreLaunchDashboard-running'>
        <Typography kind='h3' desktop='h3-d' mobile='h3-d'>
          {l('Analysis in Progress')}
        </Typography>
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          className='PreLaunchDashboard-runningSubtitle'>
          {`Run #${statusData.runNumber}`}
        </Typography>
        <ProgressBar
          pct={pct}
          status='RUNNING'
          phaseLabel={getPhaseLabel(pct, l)}
        />
        <Typography
          kind='body3'
          desktop='body3-d'
          mobile='body3-d'
          className='PreLaunchDashboard-runningHint'>
          {l(
            'This page will automatically refresh when the analysis completes.'
          )}
        </Typography>
      </Box>
    );
  }

  // ── FAILED ─────────────────────────────────────────────────────────────────
  if (statusData?.status === 'FAILED') {
    return (
      <Box className='PreLaunchDashboard-errorState'>
        <Icon
          kind='StatusError'
          size='large'
          color='--color-foreground-critical-default'
          className='PreLaunchDashboard-errorIcon'
        />
        <Typography kind='h3' desktop='h3-d' mobile='h3-d'>
          {l('Analysis Failed')}
        </Typography>
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          className='PreLaunchDashboard-errorMsg'>
          {statusData.errorMessage || l('An unexpected error occurred.')}
        </Typography>
        <Button
          type='button'
          kind='filled'
          size='medium'
          isDisabled={isTriggerLoading || !!triggerConflict}
          onClick={handleRetrigger}>
          {isTriggerLoading ? l('Retriggering…') : l('Retrigger Analysis')}
        </Button>
        {(triggerError || triggerConflict) && (
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            className='PreLaunchDashboard-inlineError'>
            {triggerConflict?.message || triggerError}
          </Typography>
        )}
      </Box>
    );
  }

  if (isReportLoading) {
    return (
      <Box className='PreLaunchDashboard-loading'>
        <Loader kind='dots' format='inline' size='medium' color='brand' />
        {l('Loading report…')}
      </Box>
    );
  }

  if (reportError) {
    return (
      <Box className='PreLaunchDashboard-errorState'>
        <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
          {reportError}
        </Typography>
      </Box>
    );
  }

  if (!reportData) return null;

  return (
    <Box className='PreLaunchDashboard'>
      <DashboardContent
        reportData={reportData}
        onDrillDown={onDrillDown}
        l={l}
      />
    </Box>
  );
}

function DashboardContent({ reportData, onDrillDown, l, isStale }) {
  const r = reportData;
  const dqMetrics = r.dataQuality || [];
  const riskMetrics = r.riskAnalysis || [];
  const certDqMetrics = r.certifierDq || [];
  const typeSpecificDqMetrics = r.typeSpecificDq || [];
  const typeSpecificRiskMetrics = r.typeSpecificRisk || [];

  return (
    <Box className={isStale ? 'PreLaunchDashboard-stale' : undefined}>
      {/* ── Aggregate Counts ──────────────────────────────────────────────── */}
      <Box tag='section' className='PreLaunchDashboard-section'>
        <Typography kind='h5' className='PreLaunchDashboard-sectionTitle'>
          {l('Aggregate Counts')}
        </Typography>
        <Box className='PreLaunchDashboard-cards' id='plAggregateCards'>
          {[
            { label: l('Users'), value: r.totalUsers },
            { label: l('Accounts'), value: r.totalAccounts },
            { label: l('Entitlements'), value: r.totalEntitlements },
            { label: l('Roles'), value: r.totalRoles },
            { label: l('Certifiers'), value: r.totalCertifiers },
          ].map(({ label, value }) => (
            <Box key={label} className='PreLaunchDashboard-card'>
              <Typography kind='body3' className='PreLaunchDashboard-cardLabel'>
                {label}
              </Typography>
              <Typography kind='h4' className='PreLaunchDashboard-cardValue'>
                {value != null && value !== '' && !Number.isNaN(Number(value))
                  ? Number(value).toLocaleString()
                  : String(value ?? '0')}
              </Typography>
            </Box>
          ))}
          {r.totalApplications != null && r.totalApplications !== '' && (
            <Box className='PreLaunchDashboard-card'>
              <Typography kind='body3' className='PreLaunchDashboard-cardLabel'>
                {l('Applications')}
              </Typography>
              <Typography kind='h4' className='PreLaunchDashboard-cardValue'>
                {Number.isNaN(Number(r.totalApplications))
                  ? String(r.totalApplications ?? '0')
                  : Number(r.totalApplications).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Data Quality + Risk Analysis ──────────────────────────────────── */}
      <Box className='PreLaunchDashboard-twoCol'>
        <Box tag='section' className='PreLaunchDashboard-section'>
          <Typography kind='h5' className='PreLaunchDashboard-sectionTitle'>
            {l('Data Quality Insights')}
          </Typography>
          <MetricTable metrics={dqMetrics} onDrillDown={onDrillDown} />
        </Box>
        <Box tag='section' className='PreLaunchDashboard-section'>
          <Typography kind='h5' className='PreLaunchDashboard-sectionTitle'>
            {l('Risk Analysis')}
          </Typography>
          <MetricTable metrics={riskMetrics} onDrillDown={onDrillDown} />
        </Box>
      </Box>

      {/* ── Certifier DQ + Type-Specific DQ ──────────────────────────────── */}
      <Box className='PreLaunchDashboard-twoCol'>
        <Box tag='section' className='PreLaunchDashboard-section'>
          <Typography kind='h5' className='PreLaunchDashboard-sectionTitle'>
            {l('Certifier Data Quality Insights')}
          </Typography>
          <MetricTable metrics={certDqMetrics} onDrillDown={onDrillDown} />
        </Box>
        {typeSpecificDqMetrics.length > 0 && (
          <Box tag='section' className='PreLaunchDashboard-section'>
            <Typography kind='h5' className='PreLaunchDashboard-sectionTitle'>
              {l('Access Lifecycle Metrics')}
            </Typography>
            <MetricTable
              metrics={typeSpecificDqMetrics}
              onDrillDown={onDrillDown}
            />
          </Box>
        )}
      </Box>

      {/* ── Type-Specific Risk Analysis ───────────────────────────────────── */}
      {typeSpecificRiskMetrics.length > 0 && (
        <Box tag='section' className='PreLaunchDashboard-section'>
          <Typography kind='h5' className='PreLaunchDashboard-sectionTitle'>
            {l('Access Risk Indicators')}
          </Typography>
          <MetricTable
            metrics={typeSpecificRiskMetrics}
            onDrillDown={onDrillDown}
          />
        </Box>
      )}
    </Box>
  );
}

DashboardContent.propTypes = {
  reportData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onDrillDown: PropTypes.func,
  l: PropTypes.func.isRequired,
  isStale: PropTypes.bool,
};

DashboardContent.defaultProps = {
  onDrillDown: null,
  isStale: false,
};

PreLaunchDashboard.propTypes = {
  onDrillDown: PropTypes.func,
};

PreLaunchDashboard.defaultProps = {
  onDrillDown: null,
};

export default PreLaunchDashboard;
